<template>
  <b-sidebar
    id="split-order-sidebar"
    v-model="showSplitFormLive"
    right
    shadow
    backdrop
    :no-close-on-backdrop="loadingSplitResult"
    no-close-on-esc
    width="1280px"
    bg-variant="white"
    z-index="1050"
    no-header
  >
    <template #default="{ hide }">
    <div class="sidebar-header mb-3 px-3">
      <b-row>
        <div class="col">
          <h5>{{ customTitle ? customTitle : defTitle }}</h5>
        </div>
        <div class="col-auto text-right">
          <b-button variant="secondary" class="mr-2" :disabled="loadingSplitResult" @click="hide">
            Cancel
          </b-button>
          <template v-if="!previewEmail && !previewSMS">
            <b-button
              v-if="splitMultiple"
              variant="warning"
              class="mr-2"
              :disabled="loadingSplitResult"
              @click="NextOrderInList"
            >
              Skip
            </b-button>
            <b-button
              variant="primary"
              :disabled="loadingSplitResult || !canConfirm"
              @click.prevent="SplitShipmentOrderConfirm"
            >
              <span v-if="splitMultiple">Split Order &amp; Open Next</span>
              <span v-else>Split Order</span>
            </b-button>
          </template>
        </div>
      </b-row>
    </div>

    <div class="px-3 pb-3">
      <section v-if="previewEmail">
        <h3>Preview Email:</h3>
        <div class="border p-3" v-html="previewEmail"></div>
        <b-button variant="secondary" class="mt-3" @click="previewEmail = ''; previewSMS = ''">
          Back to form
        </b-button>
      </section>
      <section v-else-if="previewSMS">
        <h3>Preview SMS:</h3>
        <div class="border p-3">
          <b-form-textarea rows="5" :disabled="true" :value="previewSMS" />
        </div>
        <b-button variant="secondary" class="mt-3" @click="previewEmail = ''; previewSMS = ''">
          Back to form
        </b-button>
      </section>
      <section v-else>
        <!-- Ship To + Order Group -->
        <b-row class="mb-3">
          <b-col md="6">
            <b-card header="Ship To" class="h-100" body-class="py-2">
              <div v-if="order && order.shipTo" style="line-height: 1.5;">
                <div>
                  <strong>{{ order.shipTo.name }}</strong>
                </div>
                <div v-if="order.shipTo.company">{{ order.shipTo.company }}</div>
                <div v-for="(line, idx) in shipToStreetLines" :key="idx">{{ line }}</div>
                <div>{{ shipToCityLine }}</div>
                <div v-if="order.shipTo.country">{{ order.shipTo.country }}</div>
                <div v-if="order.shipTo.phone" class="small text-muted">
                  <i class="fa fa-phone mr-1" />{{ order.shipTo.phone }}
                </div>
                <div v-if="order.shipTo.email" class="small text-muted">
                  <i class="fa fa-envelope mr-1" />{{ order.shipTo.email }}
                </div>
              </div>
              <div v-else class="text-muted">No ship to address on this order.</div>
            </b-card>
          </b-col>
          <b-col md="6">
            <b-card :header="'New Order - #' + order.orderNumber + '-1'" class="h-100" body-class="py-2">
              <b-form-group label="Order Group:" class="mb-0">
                <b-form-select v-model="orderGroupId" :options="storesListPos" />
              </b-form-group>
            </b-card>
          </b-col>
        </b-row>

        <!-- Two columns: ship now / ship later -->
        <b-row>
          <b-col md="6" class="pr-md-2">
            <div class="border rounded h-100">
              <div class="bg-primary text-white px-3 py-2 rounded-top">
                <strong><i class="fa fa-truck mr-1" /> Ship Now</strong>
                <span class="float-right">{{ notPickedOrderItemsList.length }} item(s)</span>
              </div>
              <b-table-simple small responsive hover class="mb-0">
                <b-thead>
                  <b-tr>
                    <b-th>Item</b-th>
                    <b-th class="text-right" style="width: 60px;">Qty</b-th>
                    <b-th class="text-right" style="width: 90px;" />
                  </b-tr>
                </b-thead>
                <b-tbody>
                  <b-tr v-for="itm in notPickedOrderItemsList" :key="'now-' + itm.key">
                    <b-td>
                      <div class="font-weight-bold">
                        {{ itm.name }}
                        <b-badge
                          v-if="itm.notEnoughInventory"
                          v-b-tooltip.hover="{ container: 'body' }"
                          variant="danger"
                          class="ml-1"
                          title="This order item did not have enough inventory to be fulfilled."
                        >
                          <i class="fa fa-sort-numeric-asc" /> Out of Stock
                        </b-badge>
                      </div>
                      <div class="small text-muted">SKU: {{ itm.sku }}</div>
                    </b-td>
                    <b-td class="text-right align-middle">{{ itm.quantity }}</b-td>
                    <b-td class="text-right align-middle text-nowrap">
                      <b-button
                        v-if="itm.quantity > 1"
                        size="sm"
                        variant="outline-warning"
                        title="Ship all of this item later"
                        v-b-tooltip.hover.left="{ container: 'body' }"
                        :disabled="loadingSplitResult"
                        @click="MoveToShipLater(itm.key, itm.quantity)"
                      >
                        <i class="fa fa-angle-double-right" />
                      </b-button>
                      <b-button
                        size="sm"
                        variant="outline-warning"
                        title="Ship 1 later"
                        v-b-tooltip.hover.left="{ container: 'body' }"
                        :disabled="loadingSplitResult"
                        @click="MoveToShipLater(itm.key, 1)"
                      >
                        <i class="fa fa-angle-right" />
                      </b-button>
                    </b-td>
                  </b-tr>
                  <b-tr v-if="!notPickedOrderItemsList.length">
                    <b-td colspan="3" class="p-0">
                      <div class="alert alert-danger m-2 p-2">
                        <b>No items left on original order! This is not allowed!</b>
                      </div>
                    </b-td>
                  </b-tr>
                </b-tbody>
              </b-table-simple>
            </div>
          </b-col>

          <b-col md="6" class="pl-md-2">
            <div class="border rounded h-100">
              <div class="bg-secondary px-3 py-2 rounded-top">
                <strong><i class="fa fa-clock-o mr-1" /> Ship Later (New Order)</strong>
                <span class="float-right">{{ orderItemsPicked.length }} item(s)</span>
              </div>
              <b-table-simple small responsive hover class="mb-0">
                <b-thead>
                  <b-tr>
                    <b-th style="width: 90px;" />
                    <b-th>Item</b-th>
                    <b-th class="text-right" style="width: 60px;">Qty</b-th>
                  </b-tr>
                </b-thead>
                <b-tbody>
                  <b-tr v-for="item in orderItemsPicked" :key="'later-' + item.key">
                    <b-td class="align-middle text-nowrap">
                      <b-button
                        size="sm"
                        variant="outline-success"
                        title="Ship 1 now"
                        v-b-tooltip.hover.right="{ container: 'body' }"
                        :disabled="loadingSplitResult"
                        @click="MoveToShipNow(item.key, 1)"
                      >
                        <i class="fa fa-angle-left" />
                      </b-button>
                      <b-button
                        v-if="item.quantity > 1"
                        size="sm"
                        variant="outline-success"
                        title="Ship all of this item now"
                        v-b-tooltip.hover.right="{ container: 'body' }"
                        :disabled="loadingSplitResult"
                        @click="MoveToShipNow(item.key, item.quantity)"
                      >
                        <i class="fa fa-angle-double-left" />
                      </b-button>
                    </b-td>
                    <b-td>
                      <div class="font-weight-bold">
                        {{ item.name }}
                        <b-badge
                          v-if="isNotEnoughInventory(item.sku)"
                          v-b-tooltip.hover="{ container: 'body' }"
                          variant="danger"
                          class="ml-1"
                          title="This order item did not have enough inventory to be fulfilled."
                        >
                          <i class="fa fa-sort-numeric-asc" /> Out of Stock
                        </b-badge>
                      </div>
                      <div class="small text-muted">SKU: {{ item.sku }}</div>
                    </b-td>
                    <b-td class="text-right align-middle">{{ item.quantity }}</b-td>
                  </b-tr>
                  <b-tr v-if="!orderItemsPicked.length">
                    <b-td colspan="3" class="p-0">
                      <div class="alert alert-warning m-2 p-2">
                        Use the arrows to move items you want to ship later into this column.
                      </div>
                    </b-td>
                  </b-tr>
                </b-tbody>
              </b-table-simple>
            </div>
          </b-col>
        </b-row>

        <!-- Customer notifications -->
        <div class="mt-3">
          <b-form-group label="" v-if="canOptIn1" class="mb-2">
            <b-form-checkbox v-model="shouldOptIn1" name="check-button" switch>
              Send an email to the customer with the new order details.
            </b-form-checkbox>
            <a href="#" @click.prevent="PreviewOpt1" class="small d-inline-block mr-2">[Preview]</a>
          </b-form-group>

          <b-form-group label="" v-if="canOptIn2" class="mb-2">
            <b-form-checkbox v-model="shouldOptIn2" name="check-button" switch>
              Send an SMS to the customer with the new order details.
            </b-form-checkbox>
            <a href="#" @click.prevent="PreviewOpt2" class="small d-inline-block mr-2">[Preview]</a>
          </b-form-group>
        </div>
      </section>
    </div>
    </template>
  </b-sidebar>
</template>

<script>
import { mapGetters } from 'vuex';
export default {
  components: {
  },
  props: {
    customTitle: {
      type: String,
      default: ''
    },
    splitMultiple: {
      type: Boolean,
      default: false,
    },
    splitMultipleList: {
      type: Array,
      default() {
        return []
      }
    },
    order: {
      type: Object,
      required: true
    },
    showSplitForm: {
      type: Boolean,
      required: true
    },
    prefillOrder: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  computed: {
    ...mapGetters({
      myContentTemplates: 'getMyContentTemplates',
    }),
    storesListPos() {
      return this.storesList.map(itm => ({ value: itm.id, text: itm.storeName }));
    },
    shipToStreetLines() {
      let s = this.order && this.order.shipTo;
      if (!s) {
        return [];
      }
      return [s.street1, s.street2, s.street3].filter(Boolean);
    },
    shipToCityLine() {
      let s = this.order && this.order.shipTo;
      if (!s) {
        return '';
      }
      let stateZip = [s.state, s.postalCode].filter(Boolean).join(' ');
      return [s.city, stateZip].filter(Boolean).join(', ');
    },
    canOptIn1() {
      if (this.order && this.order.projectId) {
        let found = this.myContentTemplates.find(a => a.projectId == this.order.projectId && a.eventId == 'ORDER_SPLIT_USEROPT1');
        return !!(found && found.type == 'Email');
      }
      return false;
    },
    canOptIn2() {
      if (this.order && this.order.projectId) {
        let found = this.myContentTemplates.find(a => a.projectId == this.order.projectId && a.eventId == 'ORDER_SPLIT_USEROPT2');
        return !!(found && found.type == 'SMS');
      }
      return false;
    },
    // The order's line items (minus adjustments), keyed by order item id.
    workingItems() {
      return (this.order.items || [])
        .filter(a => !a.adjustment)
        .map(itm => Object.assign({}, itm, { key: itm.id }));
    },
    notPickedOrderItemsList() {
      let arrs = [];
      JSON.parse(JSON.stringify(this.workingItems)).forEach(itm => {
        let pickdItm = this.orderItemsPicked.find(a => a.key == itm.key);
        if (pickdItm == null) {
          arrs.push(itm);
        } else {
          itm.quantity = itm.quantity - pickdItm.quantity;
          if (itm.quantity > 0) {
            arrs.push(itm);
          }
        }
      });
      return arrs;
    },
    canConfirm() {
      return this.orderItemsPicked.length > 0 && this.notPickedOrderItemsList.length > 0;
    },
    showSplitFormLive: {
      get() {
        return this.showSplitForm;
      },
      set(value) {
        this.$emit('update:showSplitForm', value);
      }
    },
  },
  watch: {
    showSplitFormLive: function(val) {
      if (val && this.order) {
        this.SplitShipmentOrder(this.order);
      }
    }
  },
  mounted() {
    if (this.prefillOrder) {
      this.prefillPickedItems();
    } else {
      this.orderItemsPicked = [];
    }
    this.orderGroupId = this.order.storeId;
    this.updateTitle();
    this.loadStoresForOrder(this.order);
  },
  methods: {
    updateTitle() {
      if (this.splitMultiple) {
        this.defTitle = 'Order ' + this.orderInList + ' / ' + this.splitMultipleList.length + ' to split - Order #' + this.order.orderNumber;
      } else {
        this.defTitle = 'Split Order - Order #' + this.order.orderNumber;
      }
    },
    prefillPickedItems() {
      let orderItemsToPrefill = this.order.items.filter(a => a.notEnoughInventory && !a.adjustment);
      this.orderItemsPicked = orderItemsToPrefill.map(itm => {
        let key = 'itm' + new Date().getUTCMilliseconds() + (itm.lineItemKey ? '-' + itm.lineItemKey : '');
        return {
          id: itm.id,
          key: itm.id,
          unitPrice: itm.unitPrice,
          originalQty: itm.quantity,
          quantity: itm.quantity,
          lineItemKey: key,
          orderId: 'tmpId',
          sku: itm.sku,
          productId: itm.productId,
          name: itm.name
        };
      });
    },
    getTemplateContent(eventId, type) {
      if (this.order && this.order.projectId) {
        let found = this.myContentTemplates.find(a => a.projectId == this.order.projectId && a.eventId == eventId);
        if (found && found.type == type) {
          return found.content;
        }
      }
      return '';
    },
    PreviewOpt1() {
      this.previewEmail = this.getTemplateContent('ORDER_SPLIT_USEROPT1', 'Email');
    },
    PreviewOpt2() {
      this.previewSMS = this.getTemplateContent('ORDER_SPLIT_USEROPT2', 'SMS');
    },
    NextOrderInList() {
      this.orderInList++;
      if (this.orderInList > this.splitMultipleList.length) {
        this.showSplitFormLive = false;
        this.$swal('Finished!', 'All Orders in the list have been split!', 'success');
      } else {
        this.$emit('update:order', this.splitMultipleList[this.orderInList - 1]);
        this.$nextTick(() => {
          this.updateTitle();
          this.orderGroupId = this.order.storeId;
          this.orderItemsPicked = [];
          if (this.prefillOrder) {
            this.prefillPickedItems();
          }
          this.SplitShipmentOrder(this.order);
        });
      }
    },
    isNotEnoughInventory(sku) {
      let orderItem = this.order.items.find(a => a.sku == sku);
      return !!(orderItem && orderItem.notEnoughInventory);
    },
    MoveToShipLater(key, qty) {
      let workItem = this.workingItems.find(a => a.key == key);
      if (!workItem) {
        return;
      }
      this.addToShipLater(workItem, qty);
    },
    addToShipLater(workItem, qty) {
      let picked = this.orderItemsPicked.find(a => a.key == workItem.key);
      if (picked) {
        picked.quantity = Math.min(picked.originalQty, parseInt(picked.quantity) + qty);
      } else {
        let id = 'itm' + new Date().getUTCMilliseconds() + '-' + workItem.key;
        this.orderItemsPicked.push({
          id: id,
          key: workItem.key,
          unitPrice: workItem.unitPrice,
          originalQty: workItem.quantity,
          quantity: Math.min(workItem.quantity, qty),
          lineItemKey: id,
          orderId: 'tmpId',
          sku: workItem.sku,
          productId: workItem.productId,
          name: workItem.name
        });
      }
    },
    MoveToShipNow(key, qty) {
      let picked = this.orderItemsPicked.find(a => a.key == key);
      if (!picked) {
        return;
      }
      let remaining = parseInt(picked.quantity) - qty;
      if (remaining > 0) {
        picked.quantity = remaining;
      } else {
        this.orderItemsPicked = this.orderItemsPicked.filter(a => a.key != key);
      }
    },
    loadStoresForOrder(order) {
      this.$http
        .post('/api/common/orderGroups/byProject/' + order.projectId)
        .then(resp => {
          this.storesList = resp.data;
        })
        .catch(() => {
          this.storesList = [];
        });
    },
    SplitShipmentOrder(order) {
      this.loadStoresForOrder(order);
    },
    SplitShipmentOrderConfirm() {
      let itemsList = this.orderItemsPicked;
      let allGood = true;
      let noItemsPicked = true;
      let noItemsLeftPicked = true;
      itemsList.forEach((it) => {
        it.quantity = parseInt(it.quantity);
        if (!(it.quantity >= 0 && it.quantity <= it.originalQty)) {
          allGood = false;
        }
        if (it.quantity > 0) {
          noItemsPicked = false;
        }
      });
      this.order.items.forEach((itm) => {
        let fndItm = itemsList.find(a => a.id == itm.id);
        if (fndItm) {
          if (fndItm.quantity > itm.quantity) {
            noItemsLeftPicked = false;
          }
        } else {
          noItemsLeftPicked = false;
        }
      });
      if (!allGood) {
        this.$swal({
          title: 'Invalid Quantity Selected!',
          text: 'You cannot split an order if you set the quantity less then 0, or if quantity is more then the original quantity.',
          icon: 'error',
        });
        return false;
      }
      if (noItemsPicked) {
        this.$swal({
          title: 'Invalid Quantity Selected!',
          text: 'You are trying to split of an order, but with 0 order items in it, please check your order items quantity.',
          icon: 'error',
        });
        return false;
      }
      if (noItemsLeftPicked) {
        this.$swal({
          title: 'Invalid Quantity Selected!',
          text: 'You are trying to split of an order, but the new order contains all the items from the original, nothing is left in the initial order!',
          icon: 'error',
        });
        return false;
      }
      this.$swal({
        title: 'Are you sure?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, split the order!'
      }).then(result => {
        if (result.value) {
          this.loadingSplitResult = true;
          this.$http
            .post('/api/common/orders/split/' + this.order.id, {
              items: itemsList,
              orderGroupId: this.orderGroupId,
              optIn1: this.shouldOptIn1,
              optIn2: this.shouldOptIn2
            })
            .then(resp => {
              if (resp.data.success) {
                this.$swal('Order Split!', 'Order was split.', 'success');
                if (!this.splitMultiple) {
                  this.showSplitFormLive = false;
                } else {
                  this.NextOrderInList();
                }
                this.$emit('split-completed', this.order);
              } else {
                if (!this.splitMultiple) {
                  this.showSplitFormLive = false;
                }
                this.$swal('Order was NOT Split!', 'Something went wrong', 'error');
              }
            })
            .catch(err => {
              if (!this.splitMultiple) {
                this.showSplitFormLive = false;
              }
              if (err.response && err.response.data && err.response.data.errorMessage) {
                this.$swal({ title: 'Order was NOT Split!', text: err.response.data.errorMessage, icon: 'error' });
              } else {
                this.$swal('Order was NOT Split!', 'Something went wrong', 'error');
              }
            })
            .finally(() => {
              this.loadingSplitResult = false;
            });
        }
      });
    },
  },
  data() {
    return {
      storesList: [],
      previewEmail: '',
      previewSMS: '',
      shouldOptIn1: false,
      shouldOptIn2: false,
      orderInList: 1,
      defTitle: 'Split Order',
      loadingSplitResult: false,
      orderGroupId: '',
      orderItemsPicked: []
    }
  }
}
</script>
